import {
    CompletionMap,
    SaveArticleBody,
    articleLagsCompletion,
    completionMapHasText,
    generationGuardsSave,
    isSaveIntegrityError,
    shouldWriteArticle,
    staleRunSaveOverwritesDone,
    withCompletionFaq,
} from '@/service/saveIntegrity';

export type GenerationSaveResult = 'hold' | 'confirmed' | 'saved';

export type GenerationSaveOptions = {
    /** Write enhanced/aiGenerateStatus done only after the stored article covers the completion. */
    markDone: boolean;
    /** Manual save of a draft that has no completion text yet. */
    allowWithoutCompletions: boolean;
    /**
     * The save that starts a run, before the server owns the document.
     * Every later client save while generation is in progress is refused.
     */
    generationStart?: boolean;
};

export type ArticleSaveIo = {
    loadCompletions(articleId: string): Promise<CompletionMap>;
    read(articleId: string): Promise<SaveArticleBody | null>;
    /** The completion check already ran in commitCoveringArticle. The write does not repeat it. */
    write(article: SaveArticleBody): Promise<void>;
    markDone(article: SaveArticleBody): Promise<void>;
};

/**
 * One integrity pass per save. A manual save of an article that generation
 * no longer owns (done, failed, never generated) writes what the editor
 * shows: one read of the stored status, no completion load, no re-read.
 * A tab that still holds a run past its limit is held when the stored
 * article is already done: its content is from before the run.
 * While generation owns the article, a save is written only when it is not
 * shorter than the completion. When the write assembles the generation
 * result (markDone), settled FAQ from the completion is part of it, and done
 * is set only after a re-read still covers the completion, including that FAQ.
 */
export async function commitCoveringArticle(
    article: SaveArticleBody & {id?: string | null},
    options: GenerationSaveOptions,
    io: ArticleSaveIo,
): Promise<GenerationSaveResult> {
    if (!article?.id) {
        if (!options.allowWithoutCompletions) {
            return 'hold';
        }
        await io.write(article);
        return 'saved';
    }
    const intent = {assemblesGeneration: options.markDone};
    const stored = await io.read(article.id);
    if (!options.generationStart && staleRunSaveOverwritesDone(article, stored)) {
        return 'hold';
    }
    if (!generationGuardsSave(stored, intent)) {
        return writeOrHold(article, io);
    }
    const completions = await io.loadCompletions(article.id);
    if (!completionMapHasText(completions)) {
        if (!options.allowWithoutCompletions) {
            return 'hold';
        }
        return writeOrHold(article, io);
    }
    const candidate = options.markDone ? withCompletionFaq(article, completions) : article;
    if (!shouldWriteArticle(candidate, stored, completions, intent)) {
        return 'hold';
    }
    if (candidate.questions !== article.questions) {
        article.questions = candidate.questions || [];
    }
    const written = await writeOrHold(article, io);
    if (written === 'hold' || !options.markDone) {
        return written;
    }
    const after = await io.read(article.id);
    if (!after || articleLagsCompletion(after, completions)) {
        return 'saved';
    }
    await io.markDone(article);
    return 'confirmed';
}

async function writeOrHold(article: SaveArticleBody, io: ArticleSaveIo): Promise<GenerationSaveResult> {
    try {
        await io.write(article);
    } catch (error) {
        if (isSaveIntegrityError(error)) {
            return 'hold';
        }
        throw error;
    }
    return 'saved';
}
